import { useState } from "react";
import { AuditTable } from "../../components/AuditTable";
import { Shell } from "../../components/Shell";
import { Button, Confirm, Empty, ErrorNotice, Field, Input, Loading, Modal, PageHead, Panel, SecretReveal, GameStatusBadge, Select, StatusBadge, useToast } from "../../components/ui";
import { planSummary } from "../../components/Upgrade";
import { adminApi } from "../../lib/api";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { Link, navigate } from "../../lib/router";
import { useClient } from "../../lib/session";
import { formatDateTime, relativeTime } from "../../lib/time";
import type { AdminDeveloper, Credential, PlanId } from "../../lib/types";

export function AdminDeveloperDetail({ id }: { id: string }) {
  const api = adminApi(useClient());
  const toast = useToast();
  const q = useQuery(`admin:developer:${id}`, () => api.developer(id));
  const audit = useQuery(`admin:audit:dev:${id}`, () => api.auditLogs({ developer_id: id, limit: 20 }));
  const [editing, setEditing] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [revoking, setRevoking] = useState<Credential | null>(null);

  const d = q.data?.developer;
  const setStatus = useAction((status: string) => api.updateDeveloper(id, { status }));
  const revoke = useAction(async (c: Credential) => {
    await api.revokeCredential(id, c.id);
    return true;
  });

  const toggleStatus = async () => {
    if (!d) return;
    const next = d.status === "active" ? "suspended" : "active";
    if (await setStatus.run(next)) {
      setSuspending(false);
      invalidate("admin:");
      toast(next === "suspended" ? `${d.name} is suspended` : `${d.name} is active again`);
    }
  };

  return (
    <Shell crumbs={[{ label: "Developers", to: "/admin/developers" }, { label: d?.name ?? "Developer" }]}>
      <ErrorNotice error={q.error} onRetry={q.reload} />
      {!d && q.loading && <Loading />}
      {d && q.data && (
        <>
          <PageHead
            title={d.name}
            badge={<StatusBadge status={d.status} />}
            lede={d.contact_email}
            actions={
              <>
                <Button onClick={() => setEditing(true)}>Edit profile</Button>
                <Button variant={d.status === "active" ? "danger" : "primary"} onClick={() => setSuspending(true)}>
                  {d.status === "active" ? "Suspend developer" : "Reactivate developer"}
                </Button>
              </>
            }
          />
          {d.status === "suspended" && (
            <div className="notice warn" style={{ marginBottom: 20 }}>
              Suspended. This developer's credentials and every API key of their games are refused until you reactivate them.
            </div>
          )}
          <div className="stack">
            <div className="grid-2">
              <Panel title="Profile">
                <dl className="facts">
                  <dt>Developer ID</dt>
                  <dd className="mono">{d.id}</dd>
                  <dt>Contact</dt>
                  <dd><a href={`mailto:${d.contact_email}`}>{d.contact_email}</a></dd>
                  <dt>Joined</dt>
                  <dd>{formatDateTime(d.created_at)}</dd>
                  <dt>Last active</dt>
                  <dd>{relativeTime(d.last_active_at)}</dd>
                </dl>
              </Panel>
              <Panel title="Footprint">
                <dl className="facts">
                  <dt>Games</dt>
                  <dd>{d.games}</dd>
                  <dt>Active developer credentials</dt>
                  <dd>{d.active_credentials}</dd>
                  <dt>Active game API keys</dt>
                  <dd>{d.active_api_keys}</dd>
                  <dt>Plan</dt>
                  <dd>
                    {planSummary(d)}{" "}
                    <Button size="small" onClick={() => setPlanOpen(true)}>Change plan</Button>
                  </dd>
                </dl>
              </Panel>
            </div>

            <Panel title="Games" flush>
              {q.data.games.length === 0 ? (
                <Empty title="No games yet">They create games after signing in with their credential.</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Game</th>
                        <th>Status</th>
                        <th className="num">Banks</th>
                        <th className="num">Player accounts</th>
                        <th className="num">Transactions</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.data.games.map((g) => (
                        <tr key={g.id} className="clickable" onClick={() => navigate(`/admin/games/${g.id}`)}>
                          <td>
                            <Link to={`/admin/games/${g.id}`} className="cell-title" onClick={(e) => e.stopPropagation()}>{g.name}</Link>
                            <div className="cell-sub mono">{g.slug}</div>
                          </td>
                          <td><GameStatusBadge status={g.status} /></td>
                          <td className="num">{g.banks.sandbox + g.banks.production}</td>
                          <td className="num">{g.player_accounts.sandbox} / {g.player_accounts.production}<div className="cell-sub">sandbox / live</div></td>
                          <td className="num">{g.transactions.sandbox + g.transactions.production}</td>
                          <td>{formatDateTime(g.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <Panel
              title="Developer credentials"
              sub="Used to sign in here and to call the management API. Issue a new one if theirs is lost, then revoke the old one."
              actions={<Button onClick={() => setIssuing(true)}>Issue credential</Button>}
              flush
            >
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Prefix</th>
                      <th>Last used</th>
                      <th>Created</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.credentials.map((c) => (
                      <tr key={c.id}>
                        <td>{c.label}</td>
                        <td className="mono">{c.prefix}</td>
                        <td>{relativeTime(c.last_used_at)}</td>
                        <td>{formatDateTime(c.created_at)}</td>
                        <td><StatusBadge status={c.revoked_at ? "revoked" : c.expires_at && new Date(c.expires_at) < new Date() ? "expired" : "active"} /></td>
                        <td className="num">
                          {!c.revoked_at && (
                            <Button size="small" variant="danger" onClick={() => setRevoking(c)}>Revoke</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title="Recent activity" actions={<Link to={`/admin/audit?developer_id=${id}`}>See all</Link>} flush>
              <ErrorNotice error={audit.error} />
              {audit.data && <AuditTable rows={audit.data.data} />}
            </Panel>
          </div>

          {planOpen && <ChangePlan dev={d} open onClose={() => setPlanOpen(false)} />}
          <EditProfile dev={d} open={editing} onClose={() => setEditing(false)} />
          <IssueCredential devId={id} name={d.name} open={issuing} onClose={() => setIssuing(false)} />
          <Confirm
            open={suspending}
            title={d.status === "active" ? `Suspend ${d.name}?` : `Reactivate ${d.name}?`}
            body={
              d.status === "active"
                ? "Their developer credentials and all game API keys stop working on the next request, in sandbox and production. Player balances are untouched."
                : "Their credentials and unrevoked game keys start working again immediately."
            }
            confirmLabel={d.status === "active" ? "Suspend developer" : "Reactivate developer"}
            danger={d.status === "active"}
            busy={setStatus.pending}
            error={setStatus.error}
            onConfirm={toggleStatus}
            onClose={() => setSuspending(false)}
          />
          <Confirm
            open={!!revoking}
            title="Revoke this credential?"
            body={<>Anything using <span className="mono">{revoking?.prefix}</span> is refused from the next request. This can't be undone.</>}
            confirmLabel="Revoke credential"
            danger
            busy={revoke.pending}
            error={revoke.error}
            onConfirm={async () => {
              if (revoking && (await revoke.run(revoking))) {
                setRevoking(null);
                invalidate(`admin:developer:${id}`);
                toast("Credential revoked");
              }
            }}
            onClose={() => setRevoking(null)}
          />
        </>
      )}
    </Shell>
  );
}

function ChangePlan({ dev, open, onClose }: { dev: AdminDeveloper; open: boolean; onClose: () => void }) {
  const api = adminApi(useClient());
  const toast = useToast();
  const [plan, setPlan] = useState<PlanId>(dev.plan === "pro" && !dev.plan_trial ? "free" : "pro");
  const [until, setUntil] = useState("");
  const save = useAction(() => api.setPlan(dev.id, { plan, expires_at: plan === "pro" && until ? new Date(`${until}T23:59:59`).toISOString() : null }));
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Change ${dev.name}'s plan`}
      description={`Now: ${planSummary(dev)}. This ends any trial. Billing isn't connected yet, so use this for paid and complimentary upgrades.`}
      onSubmit={async () => {
        if (await save.run()) {
          invalidate("admin:");
          toast(`${dev.name} is on ${plan === "pro" ? "Pro" : "Free"}`);
          onClose();
        }
      }}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" busy={save.pending}>Save plan</Button>
        </>
      }
    >
      <div className="form">
        <Field label="Plan">
          <Select value={plan} onChange={(e) => setPlan(e.target.value as PlanId)}>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
          </Select>
        </Field>
        {plan === "pro" && (
          <Field label="Until" hint="Leave empty to keep Pro until you change it. After this date they're back on Free.">
            <Input type="date" value={until} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setUntil(e.target.value)} />
          </Field>
        )}
        <ErrorNotice error={save.error} />
      </div>
    </Modal>
  );
}

function EditProfile({ dev, open, onClose }: { dev: AdminDeveloper; open: boolean; onClose: () => void }) {
  const api = adminApi(useClient());
  const toast = useToast();
  const [name, setName] = useState(dev.name);
  const [email, setEmail] = useState(dev.contact_email);
  const save = useAction(() => api.updateDeveloper(dev.id, { name: name.trim(), contact_email: email.trim() }));
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit profile"
      onSubmit={async () => {
        if (await save.run()) {
          invalidate("admin:");
          toast("Profile saved");
          onClose();
        }
      }}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" busy={save.pending}>Save profile</Button>
        </>
      }
    >
      <div className="form">
        <Field label="Studio name">
          <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
        </Field>
        <Field label="Contact email">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        <ErrorNotice error={save.error} />
      </div>
    </Modal>
  );
}

function IssueCredential({ devId, name, open, onClose }: { devId: string; name: string; open: boolean; onClose: () => void }) {
  const api = adminApi(useClient());
  const [label, setLabel] = useState("issued by platform admin");
  const [secret, setSecret] = useState<string | null>(null);
  const issue = useAction(() => api.issueCredential(devId, label.trim()));
  const close = () => {
    setSecret(null);
    issue.setError(null);
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={close}
      title={secret ? "Credential issued" : `Issue a credential for ${name}`}
      description={secret ? undefined : "Their existing credentials keep working until you revoke them."}
      onSubmit={async () => {
        if (secret) return close();
        const r = await issue.run();
        if (r) {
          setSecret(r.credential.secret ?? "");
          invalidate(`admin:developer:${devId}`);
        }
      }}
      footer={
        <>
          {!secret && <Button onClick={close}>Cancel</Button>}
          <Button type="submit" variant="primary" busy={issue.pending}>{secret ? "Done" : "Issue credential"}</Button>
        </>
      }
    >
      {secret ? (
        <SecretReveal secret={secret} what="credential" />
      ) : (
        <div className="form">
          <Field label="Label" hint="Helps you recognise it later, e.g. 'replacement for lost credential'.">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} />
          </Field>
          <ErrorNotice error={issue.error} />
        </div>
      )}
    </Modal>
  );
}
